'use client'

import { FPS } from 'creation/src/creation-tools/constants'
import getCurrentFrameCanvas from 'creation/src/creation-tools/utils/get-current-frame-canvas'
import { useEffect, useRef, useState } from 'react'
import { overlayBlock } from '../../presentation/blocks'
import Canvas from '../../presentation/canvas'
import { useCreationStore } from '../../store/provider'
import { useCutOut } from './cut-out-context'
import { useRemovingBackgroundMessage } from './removing-background-message'
import Tools from './tools'
import { cn } from '@/util/cn'

/**
 * Legacy renders shared/components/leader-board/download-animation.tsx here
 * (canvas.tsx:60-62), a green barber-pole that plays while background removal
 * is in flight. Reproduced locally rather than shared — nothing else in the
 * port uses it.
 *
 * The copy is an intentional addition (spec §12.14): legacy animates but says
 * nothing, so the user is left watching stripes with no idea what is happening
 * or that it is worth waiting for. Black on giphyGreen rather than white —
 * giphyGreen is light enough that white text on it fails contrast.
 */
const MagicWandProgress = () => {
    // Mounted only while loading, so the timer starts with the request.
    const message = useRemovingBackgroundMessage(true)

    return (
        <div
            data-testid="magic-wand-progress"
            className="absolute top-0 left-0 flex h-full w-full items-center justify-center overflow-hidden rounded-[inherit] bg-giphyGreen"
        >
            <div
                className="absolute top-0 left-0 box-content h-full w-full animate-ct-stripes pr-[60px]"
                style={{
                    backgroundImage:
                        'repeating-linear-gradient(-45deg, rgba(0,0,0,0.05), rgba(0,0,0,0.05) 10px, rgba(0,0,0,0) 10px, rgba(0,0,0,0) 20px)',
                }}
            />
            <p
                role="status"
                className="relative m-0 max-w-[80%] text-center text-[15px] font-bold text-black antialiased"
            >
                {message}
            </p>
        </div>
    )
}

type Props = {
    isLoading: boolean
}

const CutOutCanvas = ({ isLoading }: Props) => {
    const sources = useCreationStore((state) => state.sources)
    const { editCanvas } = useCutOut()
    const [scale, setScale] = useState<number>(1)
    const containerRef = useRef<HTMLDivElement>(null)

    const draw = (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, drawCount: number) => {
        if (!sources.length) return

        const { width, height } = sources[0]
        const drawFrame = getCurrentFrameCanvas(editCanvas, drawCount)
        canvas.width = width
        canvas.height = height
        drawFrame && context.drawImage(drawFrame, 0, 0)
    }

    useEffect(() => {
        if (!containerRef.current || !sources.length) return
        // set scale difference between the canvas width and it's display width.
        // this is used to calculate offsets in the tools for things like mouse event locations.
        setScale(sources[0].width / containerRef.current.scrollWidth)
    }, [containerRef, sources])

    return (
        <div>
            <div ref={containerRef} className="relative">
                <Canvas className="mb-auto block w-full" draw={draw} fps={FPS} />
                <Tools scale={scale} />
                {isLoading && (
                    <div className={cn(overlayBlock, 'opacity-50')}>
                        <MagicWandProgress />
                    </div>
                )}
            </div>
        </div>
    )
}

export default CutOutCanvas
